import React, { useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
  Skeleton,
  styled,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ProductImage, getImageUrl } from '../types/product';

interface ProductImageGalleryProps {
  images: ProductImage[];
  productName: string;
}

const ThumbnailButton = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  cursor: 'pointer',
  border: selected ? `3px solid ${theme.palette.primary.main}` : '2px solid transparent',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  '&:hover': {
    border: `3px solid ${theme.palette.primary.light}`,
  },
  '& img': {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
}));

const MainImageContainer = styled(Paper)(({ theme }) => ({
  position: 'relative',
  height: 500,
  overflow: 'hidden',
  cursor: 'zoom-in',
  backgroundColor: theme.palette.grey[100],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius * 2,
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  [theme.breakpoints.down('md')]: {
    height: 400,
  },
}));

export const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  images,
  productName,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Fallback when no images
  if (!images || images.length === 0) {
    return (
      <MainImageContainer elevation={0}>
        <Typography variant="h1" color="grey.400">
          📦
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ position: 'absolute', bottom: 16 }}
        >
          No image available
        </Typography>
      </MainImageContainer>
    );
  }

  const currentImage = images[selectedIndex];
  const hasMultipleImages = images.length > 1;

  const handlePrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setImageLoading(true);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setImageLoading(true);
  };

  const handleLightboxPrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleLightboxNext = () => {
    setSelectedIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return (
    <Box>
      {/* Main Image */}
      <MainImageContainer
        elevation={0}
        onClick={() => setLightboxOpen(true)}
        sx={{
          '&:hover .zoom-icon': {
            opacity: 1,
          },
        }}
      >
        {imageLoading && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="wave"
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
        )}

        <img
          src={getImageUrl(currentImage.large)}
          alt={`${productName} - ${selectedIndex + 1}`}
          onLoad={() => setImageLoading(false)}
          style={{ display: imageLoading ? 'none' : 'block' }}
        />

        {/* Zoom Icon */}
        <IconButton
          className="zoom-icon"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'background.paper',
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': {
              backgroundColor: 'background.paper',
            },
          }}
          size="small"
        >
          <ZoomInIcon />
        </IconButton>

        {/* Navigation Arrows (only if multiple images) */}
        {hasMultipleImages && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'background.paper',
                '&:hover': {
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>

            {/* Image Counter */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2,
                typography: 'caption',
              }}
            >
              {selectedIndex + 1} / {images.length}
            </Box>
          </>
        )}
      </MainImageContainer>

      {/* Thumbnail Navigation */}
      {hasMultipleImages && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 2,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.200',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: 4,
            },
          }}
        >
          {images.map((image, index) => (
            <ThumbnailButton
              key={image.id}
              selected={index === selectedIndex}
              onClick={() => {
                setSelectedIndex(index);
                setImageLoading(true);
              }}
              sx={{
                width: 80,
                height: 80,
                flexShrink: 0,
              }}
            >
              <img
                src={getImageUrl(image.thumbnail)}
                alt={`${productName} - Thumbnail ${index + 1}`}
              />
            </ThumbnailButton>
          ))}
        </Box>
      )}

      {/* Lightbox Modal */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Large Image */}
          <Box
            component="img"
            src={getImageUrl(currentImage.large)}
            alt={`${productName} - Full size`}
            sx={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
            }}
          />

          {/* Navigation in Lightbox */}
          {hasMultipleImages && (
            <>
              <IconButton
                onClick={handleLightboxPrevious}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>

              <IconButton
                onClick={handleLightboxNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>

              {/* Image Counter in Lightbox */}
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                {selectedIndex + 1} / {images.length}
              </Typography>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
